/**
 * HBarList: ranked list of the top items with a proportional bar (e.g. best-selling dishes).
 * Props: caption (accessible title), items [{ key, label, value }] already sorted, formatValue,
 *        valueLabel (table header, e.g. "Orders"), labelHeading?, limit? (default 10), loading?, className?.
 * The text is drawn and aria-hidden; screen readers get the hidden table.
 */
import { useTranslation } from 'react-i18next';
import { ChartState, DataTable } from './chartKit';
import { GRID, colorOf } from './chartUtils';

export interface HBarItem {
    key: string;
    label: string;
    value: number;
}

export interface HBarListProps {
    caption: string;
    items: HBarItem[];
    formatValue: (n: number) => string;
    valueLabel: string;
    labelHeading?: string;
    limit?: number;
    loading?: boolean;
    className?: string;
}

export const HBarList = ({ caption, items, formatValue, valueLabel, labelHeading, limit = 10, loading, className }: HBarListProps) => {
    const { t } = useTranslation('charts');
    const shown = items.slice(0, limit);
    const max = shown.reduce((m, i) => Math.max(m, i.value), 0);
    return (
        <div className={className}>
            <ChartState loading={loading} empty={shown.length === 0} height={160}>
                <ol aria-hidden="true" className="space-y-3">
                    {shown.map((it) => (
                        <li key={it.key}>
                            <div className="flex items-baseline justify-between gap-3 text-small">
                                <span className="min-w-0 truncate text-body">{it.label}</span>
                                <span className="shrink-0 tabular-nums text-strong">{formatValue(it.value)}</span>
                            </div>
                            <svg width="100%" height="8" focusable="false" className="mt-1 block rounded-full">
                                <rect width="100%" height="8" fill={GRID} />
                                <rect width={`${max > 0 ? (it.value / max) * 100 : 0}%`} height="8" fill={colorOf(0)} />
                            </svg>
                        </li>
                    ))}
                </ol>
            </ChartState>
            <DataTable caption={caption} headers={[labelHeading ?? t('item'), valueLabel]} rows={shown.map((i) => [i.label, formatValue(i.value)])} />
        </div>
    );
};
